// list of invoices
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CreateInvoice from '../../components/common/CreateInvoice';
import InvoiceDetails from '../../components/common/InvoiceDetails';
import InvoiceStatus from '../../components/common/InvoiceStatus';
import InvoiceStatusFilter from '../../components/common/InvoiceStatusFilter';
import InvoiceStatusUpdateDialog from '../../components/common/InvoiceStatusUpdateDialog';
import SendInvoiceDialog from '../../components/common/SendInvoiceDialog';
import InvoiceActions from '../../components/common/InvoiceActions';
import { useInvoices } from '../../context/InvoiceContext';
import { formatCurrency } from '../../utils/helper';

const InvoiceList = () => {
  // Use invoice context
  const { 
    invoices, 
    loading, 
    error, 
    fetchInvoices, 
    createInvoice, 
    updateInvoiceStatus, 
    shareInvoice, 
    addPayment, 
    getInvoiceDetails, 
    fetchCustomers
  } = useInvoices();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailsOpen, setInvoiceDetailsOpen] = useState(false);
  const [openPaymentOnMount, setOpenPaymentOnMount] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Client selection and invoice creation states
  const [clientSelectionOpen, setClientSelectionOpen] = useState(false);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  
  // Clients data from API
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  
  // Action state
  const [selectedInvoiceForAction, setSelectedInvoiceForAction] = useState(null);
  const [clientsError, setClientsError] = useState(null);
  
  // Invoice status update dialog state
  const [statusUpdateDialog, setStatusUpdateDialog] = useState({
    open: false,
    action: null, // 'mark_paid' or 'cancel'
    loading: false
  });
  
  // Send invoice dialog state
  const [sendInvoiceDialog, setSendInvoiceDialog] = useState({
    open: false,
    loading: false
  });
  
  const navigate = useNavigate();

  /** Current plan label for client (for display in Select Client modal). */
  const getClientPlanDisplay = (client) => {
    if (!client) return '';
    const plan = (client.subscriptionPlan || client.subscriptionName || '').toString().toLowerCase();
    const remainingDays = client.subscriptionExpiry?.remainingDays ?? null;
    const isPro = plan.includes('pro');
    const isExpired = remainingDays !== null && remainingDays <= 0;
    if (plan.includes('free')) return 'Free Plan';
    if (isPro && isExpired) return 'Expired Pro Plan';
    if (isPro) return 'Pro Plan';
    return client.subscriptionPlan || client.subscriptionName || 'Unknown';
  };

  // Fetch clients from API using context
  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      setClientsError(null);
      const response = await fetchCustomers();
      
      if (response.success) {
        console.log(response.data);
        // Map the API response to match the expected client structure
        // Using correct field names from CustomerListSerializer
        const mappedClients = response.data.map(customer => ({
          id: customer.id,
          companyName: customer.company_name || 'Unknown Company',
          ownerName: customer.ownerName || 'Unknown Owner',
          ownerEmail: customer.email || 'Unknown Email',
          ownerPhone: customer.phone || 'Unknown Phone',
          subscriptionPlan: customer.subscriptionPlan || customer.subscriptionName || 'Unknown Plan',
          subscriptionName: customer.subscriptionName,
          subscriptionExpiry: customer.subscriptionExpiry || null,
          totalUsers: customer.totalUsers || 0,
          maxUsersAllowed: customer.maxUsersAllowed || 0,
          businessInfo: {
            gstin: customer.gstin || 'Not provided',
            address: customer.address || 'Address not provided'
          }
        }));
        
        setClients(mappedClients);
      } else {
        setClientsError(response.message || 'Failed to fetch customers');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClientsError('Error connecting to server');
    } finally {
      setClientsLoading(false);
    }
  };



  // Show error message if there's an error
  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error'
      });
    }
  }, [error]);

  // Show clients error message
  useEffect(() => {
    if (clientsError) {
      setSnackbar({
        open: true,
        message: `Clients Error: ${clientsError}`,
        severity: 'error'
      });
    }
  }, [clientsError]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, invoices]);

  // Ensure page is within valid bounds when data changes
  useEffect(() => {
    const filtered = invoices.filter(invoice => {
      const matchesSearch = (invoice.account_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (invoice.owner_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (invoice.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Check both status and status_verbose for filtering
      const matchesStatus = statusFilter === 'all' || 
        invoice.status === statusFilter || 
        invoice.status_verbose === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
    const calculatedTotalPages = Math.ceil(filtered.length / rowsPerPage);
    if (page >= calculatedTotalPages && calculatedTotalPages > 0) {
      setPage(calculatedTotalPages - 1);
    }
  }, [invoices, searchTerm, statusFilter, rowsPerPage, page]);

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };



  const handleViewInvoice = (invoice) => {
    // Backend data should already be in the correct format
    setSelectedInvoice(invoice);
    setInvoiceDetailsOpen(true);
  };

  const handleSendInvoice = async (invoiceId) => {
    setSendInvoiceDialog(prev => ({ ...prev, loading: true }));
    try {
      const response = await shareInvoice(invoiceId);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Invoice sent successfully',
          severity: 'success'
        });
        
        // Close the dialog
        setSendInvoiceDialog({ open: false, loading: false });
      } else {
        throw new Error(response.message || 'Failed to send invoice');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      
      let errorMessage = 'Failed to send invoice';
      
      if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setSendInvoiceDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Send invoice dialog handlers
  const handleSendInvoiceDialogClose = () => {
    setSendInvoiceDialog({ open: false, loading: false });
    setSelectedInvoiceForAction(null);
  };

  const handleSendInvoiceDialogConfirm = async (invoiceId) => {
    await handleSendInvoice(invoiceId);
  };

  const handleDownloadInvoice = (invoiceId) => {
    setSnackbar({
      open: true,
      message: 'Invoice downloaded successfully',
      severity: 'success'
    });
  };


  // Action handlers

  const handleActionDownload = async (invoice) => {
    try {
      console.log('Selected invoice for download:', invoice);
      console.log('Invoice URL field:', invoice.invoice_url);
      
      // Check multiple possible URL fields
      const invoiceUrl = invoice.invoice_url || 
                        invoice.invoiceUrl || 
                        invoice.pdf_url ||
                        invoice.url;
      
      if (invoiceUrl) {
        console.log('Using invoice URL:', invoiceUrl);
        
        // Method 1: Try direct download with fetch for better error handling
        try {
          const response = await fetch(invoiceUrl);
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${invoice.invoice_number}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setSnackbar({
              open: true,
              message: `Invoice ${invoice.invoice_number} downloaded successfully`,
              severity: 'success'
            });
          } else {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        } catch (fetchError) {
          console.log('Fetch method failed, trying direct link method:', fetchError);
          
          // Method 2: Fallback to direct link method
          const link = document.createElement('a');
          link.href = invoiceUrl;
          link.download = `${invoice.invoice_number}.pdf`;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setSnackbar({
            open: true,
            message: `Invoice ${invoice.invoice_number} download initiated`,
            severity: 'success'
          });
        }
      } else {
        console.log('No invoice URL found. Available fields:', Object.keys(invoice));
        setSnackbar({
          open: true,
          message: 'Invoice PDF URL is not available. Please contact support.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download invoice: ' + error.message,
        severity: 'error'
      });
    }
  };


  // Status update dialog handlers
  const handleStatusUpdateClose = () => {
    setStatusUpdateDialog({
      open: false,
      action: null,
      loading: false
    });
    // Clear the selected invoice when dialog is closed
    setSelectedInvoiceForAction(null);
  };

  const handleStatusUpdateConfirm = async (statusData) => {
    if (!selectedInvoiceForAction) return;

    setStatusUpdateDialog(prev => ({ ...prev, loading: true }));

    try {
      const response = await updateInvoiceStatus(selectedInvoiceForAction.id, statusData);
      
      if (response.success) {
        // Show success message
        setSnackbar({
          open: true,
          message: response.message || 'Invoice status updated successfully',
          severity: 'success'
        });

        // Close the dialog
        handleStatusUpdateClose();
      } else {
        throw new Error(response.message || 'Failed to update invoice status');
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      
      let errorMessage = 'Failed to update invoice status';
      
      if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setStatusUpdateDialog(prev => ({ ...prev, loading: false }));
    }
  };



  // Client selection handlers
  const handleCreateInvoice = () => {
    setClientSelectionOpen(true);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSelectionOpen(false);
    setCreateInvoiceOpen(true);
  };

  const handleCreateInvoiceClose = () => {
    setCreateInvoiceOpen(false);
    setSelectedClient(null);
  };

  const handleCreateInvoiceSave = async (invoiceData) => {
    try {
      const response = await createInvoice(invoiceData);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Invoice created successfully',
          severity: 'success'
        });
        
        handleCreateInvoiceClose();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to create invoice',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setSnackbar({
        open: true,
        message: 'Error creating invoice',
        severity: 'error'
      });
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysOverdue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = (invoice.account_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (invoice.owner_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (invoice.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Check both status and status_verbose for filtering
    const matchesStatus = statusFilter === 'all' || 
      invoice.status === statusFilter || 
      invoice.status_verbose === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredInvoices.length / rowsPerPage);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredClients = clients.filter(client => {
    return client.companyName.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
           client.ownerName.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
           client.ownerEmail.toLowerCase().includes(clientSearchTerm.toLowerCase());
  });

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2" fontWeight="bold">
          Invoice Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateInvoice}
          >
            Create Invoice
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by customer, email, or invoice number..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InvoiceStatusFilter
                value={statusFilter}
                onChange={setStatusFilter}
                fullWidth
                showIcons={false}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <Paper
                  elevation={0}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      mr: 1.5,
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    size="small"
                    sx={{ 
                      minWidth: '65px',
                      height: '30px',
                      mr: 3,
                      '& .MuiSelect-select': {
                        py: 0.5,
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'transparent'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider'
                      }
                    }}
                    onChange={handleChangeRowsPerPage}
                    variant="outlined"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      mx: 2
                    }}
                  >
                    {filteredInvoices.length > 0 
                      ? `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredInvoices.length)} of ${filteredInvoices.length}`
                      : '0-0 of 0'
                    }
                  </Typography>
                  <Box sx={{ 
                    display: 'flex',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    pl: 1
                  }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePageChange(page - 1)} 
                      disabled={page === 0}
                      sx={{ 
                        color: page === 0 ? 'text.disabled' : 'primary.main'
                      }}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePageChange(page + 1)} 
                      disabled={page >= totalPages - 1}
                      sx={{ 
                        color: page >= totalPages - 1 ? 'text.disabled' : 'primary.main'
                      }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Issue Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Paid Amount</TableCell>
                    <TableCell>Balance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Shared On</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {currentInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          No invoices found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm || statusFilter !== 'all' ? 'No invoices match your search criteria.' : 'There are no invoices available.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentInvoices.map((invoice) => (
                  <TableRow 
                    key={invoice.id}
                    onClick={() => handleViewInvoice(invoice)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                          {invoice.invoice_number}
                        </Typography>
                        {invoice.account_subscription && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="primary.main" fontWeight="medium">
                              {invoice.account_subscription.plan_name || 'Unknown Plan'}
                            </Typography>
                            {invoice.account_subscription.expiry && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                • {invoice.account_subscription.expiry}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {invoice.account_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {invoice.owner_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.created_at}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {invoice.due_date}
                        </Typography>
                        {invoice.status === 'OV' && (
                          <Typography variant="caption" color="error">
                            {getDaysOverdue(invoice.due_date)} days overdue
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(invoice.taxable_invoice_value)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        color={invoice.total_paid_amount > 0 ? 'success.main' : 'text.secondary'}
                      >
                        {formatCurrency(invoice.total_paid_amount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        color={invoice.status !== 'PD' ? 'error' : 'success'}
                      >
                        {invoice.status === 'PD' ? formatCurrency(0) : formatCurrency(parseFloat(invoice.taxable_invoice_value) - parseFloat(invoice.total_paid_amount || 0))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <InvoiceStatus 
                        status={invoice.status}
                        status_verbose={invoice.status_verbose}
                      />
                    </TableCell>
                    <TableCell>
                      {invoice.shared_on ? (
                        <Typography variant="body2">
                          {invoice.shared_on}
                        </Typography>
                      ) : (
                        <Chip
                          label="Not shared"
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.75rem',
                            height: 24,
                            color: 'text.secondary',
                            borderColor: 'grey.300'
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <InvoiceActions
                        invoice={invoice}
                        onView={() => handleViewInvoice(invoice)}
                        onMail={() => {
                          setSelectedInvoiceForAction(invoice);
                          setSendInvoiceDialog({ open: true, loading: false });
                        }}
                        onDownload={() => handleActionDownload(invoice)}
                        onAddPayment={() => {
                          setSelectedInvoice(invoice);
                          setOpenPaymentOnMount(true);
                          setInvoiceDetailsOpen(true);
                        }}
                        onCancel={() => {
                          setSelectedInvoiceForAction(invoice);
                          setStatusUpdateDialog({ open: true, action: 'cancel', loading: false });
                        }}
                        onMarkPaid={() => {
                          setSelectedInvoiceForAction(invoice);
                          setStatusUpdateDialog({ open: true, action: 'mark_paid', loading: false });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  ))
                )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>


      {/* Client Selection Modal */}
      <Dialog
        open={clientSelectionOpen}
        onClose={() => setClientSelectionOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BusinessIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold">
              Select Client
            </Typography>
          </Box>
          <IconButton onClick={() => setClientSelectionOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            placeholder="Search clients by company name, owner name, or email..."
            value={clientSearchTerm}
            onChange={(e) => setClientSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {clientsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : clientsError ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="error" variant="body2">
                  {clientsError}
                </Typography>
                <Button onClick={fetchClients} sx={{ mt: 1 }}>
                  Retry
                </Button>
              </Box>
            ) : filteredClients.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  {clientSearchTerm ? 'No clients match your search.' : 'No clients available.'}
                </Typography>
              </Box>
            ) : (
              filteredClients.map((client) => {
                const planLabel = getClientPlanDisplay(client);
                const isFree = planLabel === 'Free Plan';
                const isExpiredPro = planLabel === 'Expired Pro Plan';
                const avatarColor = isFree ? 'warning.main' : isExpiredPro ? 'error.main' : 'primary.main';
                return (
              <ListItem
                key={client.id}
                button
                onClick={() => handleClientSelect(client)}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': {
                    backgroundColor: 'primary.50',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: avatarColor }}>
                    <BusinessIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {client.companyName}
                      </Typography>
                      <Chip
                        label={planLabel}
                        size="small"
                        variant="outlined"
                        color={isFree ? 'warning' : isExpiredPro ? 'error' : 'primary'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Typography variant="body2" color="text.secondary" component="div">
                        {client.ownerName} • {client.ownerEmail}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        GSTIN: {client.businessInfo?.gstin || 'Not provided'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        {client.businessInfo?.address || 'Address not provided'}
                      </Typography>
                    </React.Fragment>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
                );
              })
            )}
          </List>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal */}
      <CreateInvoice
        open={createInvoiceOpen}
        onClose={handleCreateInvoiceClose}
        // onSave={handleCreateInvoiceSave}
        preSelectedCustomer={selectedClient}
        customers={clients}
      />

      {/* Invoice Details Modal */}
      <InvoiceDetails
        open={invoiceDetailsOpen}
        onClose={async () => {
          setInvoiceDetailsOpen(false);
          setOpenPaymentOnMount(false);
          // Refresh invoice data when dialog closes
          // await fetchInvoices();
        }}
        invoice={selectedInvoice}
        openPaymentDialogOnMount={openPaymentOnMount}
        showSuccessMessage={(message) => {
          setSnackbar({
            open: true,
            message: message,
            severity: 'success'
          });
        }}
      />

      {/* Invoice Status Update Dialog */}
      <InvoiceStatusUpdateDialog
        open={statusUpdateDialog.open}
        onClose={handleStatusUpdateClose}
        onConfirm={handleStatusUpdateConfirm}
        invoice={selectedInvoiceForAction}
        action={statusUpdateDialog.action}
        loading={statusUpdateDialog.loading}
      />

      {/* Send Invoice Dialog */}
      <SendInvoiceDialog
        open={sendInvoiceDialog.open}
        onClose={handleSendInvoiceDialogClose}
        onConfirm={handleSendInvoiceDialogConfirm}
        invoice={selectedInvoiceForAction}
        loading={sendInvoiceDialog.loading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceList;